import lazyRequire from "../../../util/lazyRequire";
import * as _ from "lodash";
import * as React from "react";
import { generate as shortid } from "shortid";
const PackeryLib = lazyRequire(() => ({
    Packery: require("packery"),
}));
const LAYOUT_SETTLE_MS = 5000;
function setEqual(lhs, rhs) {
    // catch case where lhs is a subset or superset of rhs
    if (lhs.size !== rhs.size) {
        return false;
    }
    // if they are the same size and each element from lhs exists
    // in rhs they have to be the same. This is a set (no duplicates)
    // after all
    return Array.from(lhs.keys()).find((lKey) => !rhs.has(lKey)) === undefined;
}
/**
 * wrapper for packery
 *
 * @class Packery
 * @extends {React.Component<IProps, {}>}
 */
class Packery extends React.Component {
    mPackery;
    mLayoutTimer;
    mRefreshTimer;
    mChildren;
    mMounted = 0;
    constructor(props) {
        super(props);
        this.mChildren = new Set(React.Children.map(props.children, (child) => child.key));
    }
    componentDidUpdate() {
        // TODO: If we layout here without condition packery end up in an endless loop were every
        //   layout triggers didUpdate again
        //   But on startup we need several layout calls before the the layout has actually settled
        //   and everything is where it should be. Haven't found a nice solution yet
        if (Date.now() - this.mMounted < LAYOUT_SETTLE_MS) {
            this.scheduleLayout();
        }
    }
    componentDidMount() {
        this.mMounted = Date.now();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextChildren = new Set(React.Children.map(nextProps.children, (child) => child.key));
        if (nextProps.totalWidth !== this.props.totalWidth ||
            !setEqual(this.mChildren, nextChildren)) {
            this.mChildren = nextChildren;
            this.scheduleRefresh();
        }
        if (nextProps.settings !== this.props.settings) {
            this.scheduleLayout();
        }
        if (!_.isEqual(nextProps.items, this.props.items)) {
            this.scheduleLayout();
        }
    }
    componentWillUnmount() {
        clearTimeout(this.mRefreshTimer);
        this.mPackery = undefined;
    }
    render() {
        const { children, totalWidth } = this.props;
        const id = shortid();
        return (React.createElement("div", { id: id, ref: this.refContainer }, React.Children.map(children, (child) => React.cloneElement(child, {
            totalWidth,
            packery: this.mPackery,
            onUpdateLayout: this.onUpdateLayout,
        }))));
    }
    refContainer = (ref) => {
        // gutter is manually implemented in css as a padding, that way it
        // can access variables
        const options = {
            itemSelector: ".packery-item",
            gutter: 0,
            percentPosition: false,
            stamp: ".stamp",
            // isHorizontal: true,
        };
        if (ref !== null) {
            // there are no typings for current packery version
            this.mPackery = new PackeryLib.Packery(ref, options);
            this.mPackery.on("layoutComplete", this.saveLayout);
            this.mPackery.on("dragItemPositioned", (draggedItem) => {
                this.onUpdateLayout();
                // this.scheduleLayout();
            });
            this.scheduleRefresh();
        }
        else {
            this.mPackery = undefined;
        }
    };
    byPosition = (lhs, rhs) => {
        return lhs.y !== rhs.y ? lhs.y - rhs.y : lhs.x - rhs.x;
    };
    fixedOrder() {
        return this.mPackery
            .getItemElements()
            .map((ch) => this.mPackery.getItem(ch))
            .sort((lhs, rhs) => this.byPosition(lhs.position, rhs.position));
    }
    onUpdateLayout = () => {
        if (this.mPackery === undefined) {
            return;
        }
        this.mPackery._resetLayout();
        // errors indicates that reset layout may trigger a callback that unmounts this control,
        // unsetting mPackery
        if (this.mPackery !== undefined) {
            this.mPackery.layoutItems(this.fixedOrder(), true);
        }
    };
    saveLayout = (items) => {
        if (items.length === 0) {
            return;
        }
        const ordered = this.fixedOrder().map((item) => item.element.id);
        // TODO: this gets called a lot, is that a bug?
        this.props.onChangeLayout(ordered);
    };
    scheduleLayout() {
        if (this.mLayoutTimer !== undefined) {
            clearTimeout(this.mLayoutTimer);
        }
        this.mLayoutTimer = setTimeout(() => {
            this.mLayoutTimer = undefined;
            if (this.mPackery !== undefined) {
                this.mPackery.layout();
            }
        }, 50);
    }
    scheduleRefresh() {
        if (this.mRefreshTimer !== undefined) {
            clearTimeout(this.mRefreshTimer);
        }
        // Cancel pending layout — the refresh will schedule its own layout
        // after reloading items. Running layout before reload causes Packery
        // to position items based on a stale item list, leading to overlap.
        if (this.mLayoutTimer !== undefined) {
            clearTimeout(this.mLayoutTimer);
            this.mLayoutTimer = undefined;
        }
        this.mRefreshTimer = setTimeout(() => {
            this.mRefreshTimer = undefined;
            if (this.mPackery !== undefined) {
                this.mPackery.reloadItems();
                this.forceUpdate();
                this.scheduleLayout();
                if (this.mPackery !== undefined &&
                    this.props.items.length !== this.mPackery.getItemElements().length) {
                    // almost certainly a bug, refresh again
                    this.scheduleRefresh();
                }
            }
            else {
                this.scheduleRefresh();
            }
        }, 50);
    }
}
export default Packery;
